import {
  Alert,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import { debounce } from 'throttle-debounce';
import { FC } from 'react';
import { StyledPaper } from '../theme';
import LoadingSpinner from '../LoadingSpinner';
import { AccessTime, ArrowDownward, ArrowUpward, CheckCircle, Clear, Search } from '@mui/icons-material';
import { useOverviewState } from './useOverviewState';
import { useGuideMigrations } from './useGuideMigrations';
import { GuideRow } from './GuideRow';
import { ExcelExport } from './ExcelExport';
import { SortColumn, SortDirection } from './types';

const SortableTableCell: FC<{
  column: SortColumn;
  currentSort: SortColumn;
  direction: SortDirection;
  onSort: (column: SortColumn) => void;
  children: React.ReactNode;
  sx?: object;
}> = ({ column, currentSort, direction, onSort, children, sx }) => {
  const isActive = currentSort === column;
  return (
    <TableCell
      sx={{
        fontWeight: 'bold',
        cursor: 'pointer',
        userSelect: 'none',
        '&:hover': { bgcolor: 'grey.100' },
        ...sx,
      }}
      onClick={() => onSort(column)}>
      <Stack direction="row" alignItems="center" gap={0.5}>
        {children}
        {isActive ? (
          direction === 'asc' ? (
            <ArrowUpward fontSize="small" sx={{ opacity: 0.7 }} />
          ) : (
            <ArrowDownward fontSize="small" sx={{ opacity: 0.7 }} />
          )
        ) : (
          <ArrowUpward fontSize="small" sx={{ opacity: 0 }} />
        )}
      </Stack>
    </TableCell>
  );
};

const DebouncedSearchField: FC<{ initialValue: string; onSearch: (query: string) => void }> = ({ initialValue, onSearch }) => {
  const [value, setValue] = useState(initialValue);

  const debouncedOnSearch = useMemo(() => debounce(300, (query: string) => onSearch(query)), [onSearch]);

  const handleChange = (newValue: string) => {
    setValue(newValue);
    debouncedOnSearch(newValue);
  };

  const handleClear = () => {
    setValue('');
    onSearch('');
  };

  return (
    <TextField
      size="small"
      placeholder="Søk etter nummer eller tittel..."
      value={value}
      onChange={(e) => handleChange(e.target.value)}
      sx={{ minWidth: 280 }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <Search fontSize="small" color="action" />
          </InputAdornment>
        ),
        endAdornment: value && (
          <InputAdornment position="end">
            <IconButton size="small" onClick={handleClear}>
              <Clear fontSize="small" />
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );
};

export const Overview: FC = () => {
  const { toggleGuide, isOpen, statusFilter, setStatusFilter, searchQuery, setSearchQuery, sortColumn, sortDirection, setSort, ownerFilter, setOwnerFilter } =
    useOverviewState();
  const { filteredGroups, ownerNames, stats, loading } = useGuideMigrations(statusFilter, searchQuery, sortColumn, sortDirection, ownerFilter);

  if (loading) {
    return (
      <StyledPaper>
        <Stack gap={3}>
          <Typography variant="h5">Migrering av utgåtte anvisninger</Typography>
          <LoadingSpinner />
        </Stack>
      </StyledPaper>
    );
  }

  return (
    <StyledPaper>
      <Stack gap={3}>
        <Typography variant="h5">Migrering av utgåtte anvisninger</Typography>

        <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
          <Stack direction="row" gap={4} alignItems="center" flexWrap="wrap">
            <Stack direction="row" gap={1} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                Anvisninger:
              </Typography>
              <Typography variant="body2" fontWeight="medium">
                {stats.completedGuides} / {stats.totalGuides} fullført
              </Typography>
            </Stack>
            <Stack direction="row" gap={1} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                Versjoner:
              </Typography>
              <Typography variant="body2" fontWeight="medium">
                {stats.totalPublished} / {stats.totalMigrations} publisert
              </Typography>
            </Stack>
            <Stack direction="row" gap={1} alignItems="center" sx={{ minWidth: 200, flex: 1, maxWidth: 300 }}>
              <LinearProgress variant="determinate" value={stats.progressPercent} sx={{ flex: 1, height: 8, borderRadius: 1 }} />
              <Typography variant="body2" fontWeight="medium">
                {stats.progressPercent}%
              </Typography>
            </Stack>
            <ExcelExport groups={filteredGroups} sx={{ ml: 'auto' }} />
          </Stack>
        </Paper>

        {stats.completedGuides === stats.totalGuides && stats.totalGuides > 0 && (
          <Alert severity="success" icon={<CheckCircle />}>
            Alle anvisninger er fullført!
          </Alert>
        )}

        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <ToggleButtonGroup value={statusFilter} exclusive onChange={(_, value) => value && setStatusFilter(value)} size="small">
            <ToggleButton value="all">Alle ({stats.totalGuides})</ToggleButton>
            <ToggleButton value="unpublished">
              <AccessTime fontSize="small" sx={{ mr: 0.5 }} />
              Ikke publisert ({stats.incompleteGuides})
            </ToggleButton>
            <ToggleButton value="published">
              <CheckCircle fontSize="small" sx={{ mr: 0.5 }} />
              Publisert ({stats.completedGuides})
            </ToggleButton>
          </ToggleButtonGroup>
          <Stack direction="row" gap={2} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Ansvarlig</InputLabel>
              <Select value={ownerFilter} label="Ansvarlig" onChange={(e) => setOwnerFilter(e.target.value)}>
                <MenuItem value="">Alle</MenuItem>
                {ownerNames.map((name) => (
                  <MenuItem key={name} value={name}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <DebouncedSearchField initialValue={searchQuery} onSearch={setSearchQuery} />
          </Stack>
        </Stack>

        <TableContainer component={Paper} variant="outlined">
          <Table aria-label="migrations table" size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ width: 50 }} />
                <SortableTableCell column="docName" currentSort={sortColumn} direction={sortDirection} onSort={setSort}>
                  Nummer
                </SortableTableCell>
                <SortableTableCell column="title" currentSort={sortColumn} direction={sortDirection} onSort={setSort}>
                  Tittel
                </SortableTableCell>
                <SortableTableCell column="ownerName" currentSort={sortColumn} direction={sortDirection} onSort={setSort}>
                  Ansvarlig
                </SortableTableCell>
                <SortableTableCell column="status" currentSort={sortColumn} direction={sortDirection} onSort={setSort} sx={{ width: 180 }}>
                  Status
                </SortableTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredGroups.map((group) => (
                <GuideRow key={group.guideId} group={group} isOpen={isOpen(group.guideId)} onToggle={() => toggleGuide(group.guideId)} />
              ))}
              {filteredGroups.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">Ingen anvisninger å vise</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>
    </StyledPaper>
  );
};
